/**
 * Centralised, typed TanStack Query key factory.
 *
 * POLICY: every key for tenant data embeds the org id as its FIRST segment.
 * Org switching currently hard-reloads the app (so the cache is wiped on switch),
 * but keys must NOT DEPEND on that reload to stay correct — embedding the org id
 * makes tenant isolation explicit in the cache and survives any future move to
 * soft org switching. The org id is read from the established source
 * (`useAuth().user?.activeOrganizationId`, the same value `useCompanyApi()` uses);
 * pass `?? null` when it may be absent (the value is stable per page load).
 *
 * Non-tenant keys (platform / super-admin / Better-Auth session / public invite)
 * intentionally do NOT take an org id — they are not scoped to the active tenant.
 * Super-admin company views are keyed by the EXPLICIT target company id, which is
 * the entity being inspected, not the viewer's active org.
 *
 * Every queryKey AND its matching invalidateQueries call must go through this
 * factory so a query and its invalidation can never drift apart.
 */

/** The org id segment. `null` is tolerated (stable per page load; org switch hard-reloads). */
type TOrgId = string | null

export const queryKeys = {
  // ---------------------------------------------------------------------------
  // Tenant-scoped — org id is ALWAYS the first segment.
  // ---------------------------------------------------------------------------

  /** Meeting list for the active org. `showHidden` toggles the hidden filter. */
  meetings: (orgId: TOrgId, showHidden?: boolean) =>
    showHidden === undefined ? (["meetings", orgId] as const) : (["meetings", orgId, showHidden] as const),
  /** Action-item summary rollup across the active org's meetings. */
  actionItemsSummary: (orgId: TOrgId) => ["meetings", orgId, "action-items", "summary"] as const,
  /** Access-scoped list stats (total / recorded minutes / this month) for the cockpit. */
  meetingStats: (orgId: TOrgId, showHidden?: boolean) =>
    showHidden === undefined
      ? (["meetings", orgId, "stats"] as const)
      : (["meetings", orgId, "stats", showHidden] as const),
  /** A single meeting's analysis payload. */
  meeting: (orgId: TOrgId, meetingId: string) => ["meeting", orgId, meetingId] as const,
  /** Full transcript for a meeting. */
  transcript: (orgId: TOrgId, meetingId: string) => ["transcript", orgId, meetingId] as const,
  /** Field-level edit history for a meeting. */
  fieldEdits: (orgId: TOrgId, meetingId: string) => ["field-edits", orgId, meetingId] as const,
  /** Access candidates (members eligible for a private meeting). */
  meetingCandidates: (orgId: TOrgId, meetingId: string) => ["meeting-candidates", orgId, meetingId] as const,
  /** A meeting's analysis translated into a target locale at a given version. */
  meetingTranslation: (orgId: TOrgId, meetingId: string, locale: string, version: number) =>
    ["meeting-translation", orgId, meetingId, locale, version] as const,

  /** Files attached to a meeting (whiteboard snaps + archive docs). */
  meetingMaterials: (orgId: TOrgId, meetingId: string) => ["meeting-materials", orgId, meetingId] as const,
  /** Visual-report fit suggestion for a meeting (the offer banner's "suggested" state). */
  reportFit: (orgId: TOrgId, meetingId: string) => ["report-fit", orgId, meetingId] as const,
  /** Generated visual reports for a meeting (which report types already exist). */
  visualReports: (orgId: TOrgId, meetingId: string) => ["visual-reports", orgId, meetingId] as const,
  /** Pre-click cost estimate for generating a report of a given type. */
  reportEstimate: (orgId: TOrgId, meetingId: string, type: string | null) =>
    ["report-estimate", orgId, meetingId, type ?? null] as const,

  /**
   * Jargon suggestions. Scope: a status (`"pending"` / `"accepted"` / `"dismissed"`),
   * a meeting id, or the whole namespace (no scope). `limit` distinguishes the
   * growing "load more" pages of the manager queue; `sort` (`"newest"` /
   * `"frequency"`) keeps those pages from colliding. Omit scope to invalidate
   * every jargon query at once.
   */
  jargonSuggestions: (orgId: TOrgId, scope?: string, limit?: number, sort?: string) =>
    scope === undefined
      ? (["jargon-suggestions", orgId] as const)
      : limit === undefined
        ? (["jargon-suggestions", orgId, scope] as const)
        : sort === undefined
          ? (["jargon-suggestions", orgId, scope, limit] as const)
          : (["jargon-suggestions", orgId, scope, limit, sort] as const),
  /** Vocabulary / glossary entries for the active org. */
  vocab: (orgId: TOrgId) => ["vocab", orgId] as const,
  /** Per-term usage stats (hits, rank, last heard). Prefix-invalidated with `vocab`. */
  vocabStats: (orgId: TOrgId) => ["vocab", orgId, "stats"] as const,

  /** Tenant settings (LLM / models / transcription / image / fallback / prompt). */
  settings: (orgId: TOrgId) => ["settings", orgId] as const,
  /** Effective (resolved) report types for the active org — the Report Types admin panel. */
  reportTypes: (orgId: TOrgId) => ["report-types", orgId] as const,
  /** Per-company meeting-summary block layout (the curate panel). */
  summaryLayout: (orgId: TOrgId) => ["summary-layout", orgId] as const,
  /** Adapter availability for the active org. */
  adapters: (orgId: TOrgId) => ["adapters", orgId] as const,
  /** Transcription provider failover health (circuit-breaker, as seen by the
   *  answering pod) for the active org — polled by the Provider-order panel. */
  adapterCircuitBreaker: (orgId: TOrgId) => ["adapters", orgId, "circuit-breaker"] as const,
  /** Per-provider "does a key actually resolve" truth (org BYOK → platform →
   *  env) for the Provider-order panel's skip warnings. */
  adapterKeyStatus: (orgId: TOrgId) => ["adapters", orgId, "key-status"] as const,
  /** Image-generation models available to the active org. */
  imageModels: (orgId: TOrgId) => ["image-models", orgId] as const,
  /** LLM models (rate-card + capability metadata) for the Model selection panel.
   *  `orgId` null → the platform-scope admin list. */
  llmModels: (orgId: TOrgId) => ["llm-models", orgId] as const,
  /** Spend caps / budgets for the active org. */
  budgets: (orgId: TOrgId) => ["budgets", orgId] as const,
  /** Cost / usage rollup for a date range. */
  usage: (orgId: TOrgId, from: string, to: string) => ["usage", orgId, from, to] as const,
  /** Tenant credit-wallet balance (credits only). */
  creditWallet: (orgId: TOrgId) => ["credits", orgId, "wallet"] as const,
  /** Paginated credit ledger for the active org — page/size are part of the key. */
  creditTransactions: (orgId: TOrgId, limit: number, offset: number) =>
    ["credits", orgId, "transactions", limit, offset] as const,
  /** Credit consumption statement for a date range (per category + per meeting). */
  creditStatement: (orgId: TOrgId, from: string, to: string) => ["credits", orgId, "statement", from, to] as const,
  /** Semantic + lexical search across meeting content. Every filter dimension is
   * part of the key so a filter change is a distinct cache entry. */
  search: (
    orgId: TOrgId,
    q: string,
    channels: readonly string[] | undefined,
    speaker: string | undefined,
    from: string | undefined,
    to: string | undefined,
  ) => ["search", orgId, q, channels ?? null, speaker ?? null, from ?? null, to ?? null] as const,
  /** Ask-AI chat sessions for the active org + caller (sidebar list). */
  askSessions: (orgId: TOrgId) => ["ask-sessions", orgId] as const,
  /** A single Ask-AI chat session with its message history. */
  askSession: (orgId: TOrgId, sessionId: string) => ["ask-session", orgId, sessionId] as const,
  /** Suggested example questions for the Ask-AI empty state. */
  askExamples: (orgId: TOrgId) => ["ask-examples", orgId] as const,
  /** Tenant audit log. */
  audit: (orgId: TOrgId) => ["audit", orgId] as const,
  /** Company settings as seen by the New Meeting dialog (language seed). */
  companySettingsForNewMeeting: (orgId: TOrgId) => ["company-settings-newmeeting", orgId] as const,
  /** Active-org UI locale preference. */
  companyUiLocale: (orgId: TOrgId) => ["company-ui-locale", orgId] as const,
  /** Recurring-series summary cards for the active org. */
  seriesSummary: (orgId: TOrgId) => ["series-summary", orgId] as const,

  // Voxcanon tenant data (series / world / canon) — still tenant-scoped, still
  // org-id-first. The campaign ROUTES are out of scope this week, but the keys
  // follow the same policy.
  /** Series list for the active org. */
  series: (orgId: TOrgId) => ["series", orgId] as const,
  /** A single series. */
  seriesDetail: (orgId: TOrgId, seriesId: string) => ["series", orgId, seriesId] as const,
  /** A series' characters. */
  seriesCharacters: (orgId: TOrgId, seriesId: string) => ["series", orgId, seriesId, "characters"] as const,
  /** A series' members. */
  seriesMembers: (orgId: TOrgId, seriesId: string) => ["series", orgId, seriesId, "members"] as const,
  /** World NPCs for a series. */
  worldNpcs: (orgId: TOrgId, seriesId: string) => ["world-npcs", orgId, seriesId] as const,
  /** World locations for a series. */
  worldLocations: (orgId: TOrgId, seriesId: string) => ["world-locations", orgId, seriesId] as const,
  /** World factions for a series. */
  worldFactions: (orgId: TOrgId, seriesId: string) => ["world-factions", orgId, seriesId] as const,
  /** World relationships for a series. */
  worldRelationships: (orgId: TOrgId, seriesId: string) => ["world-relationships", orgId, seriesId] as const,
  /** Canon journal entries for a series; optional `scope` (e.g. `"pending"`). */
  canonJournal: (orgId: TOrgId, seriesId: string, scope?: string) =>
    scope === undefined
      ? (["canon-journal", orgId, seriesId] as const)
      : (["canon-journal", orgId, seriesId, scope] as const),
  /** Canon chronicle entries for a series; optional `scope` (e.g. `"pending"`). */
  canonChronicle: (orgId: TOrgId, seriesId: string, scope?: string) =>
    scope === undefined
      ? (["canon-chronicle", orgId, seriesId] as const)
      : (["canon-chronicle", orgId, seriesId, scope] as const),
  /** Canon characters for a series. */
  canonCharacters: (orgId: TOrgId, seriesId: string) => ["canon-characters", orgId, seriesId] as const,
  /** Accepted chronicle entries for a series. */
  campaignChronicleAccepted: (orgId: TOrgId, seriesId: string) =>
    ["campaign-chronicle-accepted", orgId, seriesId] as const,
  /** Accepted journal entries for a character within a series. */
  characterJournalAccepted: (orgId: TOrgId, seriesId: string, characterId: string) =>
    ["character-journal-accepted", orgId, seriesId, characterId] as const,

  // TTRPG live + reports (meeting / series scoped, tenant data).
  /** TTRPG participants for a meeting. */
  ttrpgParticipants: (orgId: TOrgId, meetingId: string) => ["ttrpg-participants", orgId, meetingId] as const,
  /** TTRPG characters for a series. */
  ttrpgCharacters: (orgId: TOrgId, seriesId: string) => ["ttrpg-characters", orgId, seriesId] as const,
  /** Generated visuals for a meeting. */
  ttrpgVisuals: (orgId: TOrgId, meetingId: string) => ["ttrpg-visuals", orgId, meetingId] as const,
  /** Post-session reports for a meeting. */
  ttrpgReports: (orgId: TOrgId, meetingId: string) => ["ttrpg-reports", orgId, meetingId] as const,
  /** Live scene insights for a meeting. */
  sceneInsights: (orgId: TOrgId, meetingId: string) => ["scene-insights", orgId, meetingId] as const,

  // Speaker recognition (tenant data, keyed by the active company id).
  /** Enrolled speakers for a company. `q` scopes a picker search. */
  companySpeakers: (orgId: TOrgId, q?: string) =>
    q === undefined ? (["company-speakers", orgId] as const) : (["company-speakers", orgId, "q", q] as const),
  /** Enrollment clips for a specific speaker in a company. */
  speakerEnrollments: (orgId: TOrgId, speakerId: string) => ["speaker-enrollments", orgId, speakerId] as const,

  // ---------------------------------------------------------------------------
  // Public / per-session (a meeting's PUBLIC viewer surface — no auth org).
  // ---------------------------------------------------------------------------

  /** Public visuals for a meeting (DM viewer, no tenant scope). */
  ttrpgVisualsPublic: (meetingId: string) => ["ttrpg-visuals-public", meetingId] as const,
  /** Public invitation lookup by token (pre-auth). */
  invitation: (invitationId: string) => ["invitation", invitationId] as const,
  /** Registered OAuth client shown on the Connect Claude consent page (session-scoped). */
  oauthClient: (clientId: string) => ["oauth-client", clientId] as const,

  // ---------------------------------------------------------------------------
  // Better-Auth session / per-user (not tenant data).
  // ---------------------------------------------------------------------------

  /** Better-Auth session. */
  session: () => ["session"] as const,
  /** Unread-notification count for the signed-in user. */
  notificationCount: () => ["notification-count"] as const,
  /** Notification list for the signed-in user. */
  notifications: () => ["notifications"] as const,
  /** Per-user theme preference. */
  userThemePreference: () => ["user-theme-preference"] as const,
  /** Org members for the signed-in user's active org (Better-Auth org plugin). */
  orgMembers: () => ["org-members"] as const,
  /** Pending org invites (Better-Auth org plugin). */
  orgInvites: () => ["org-invites"] as const,

  // ---------------------------------------------------------------------------
  // Platform / super-admin (not active-tenant scoped). Keyed by the EXPLICIT
  // target entity id where applicable — the company being inspected, not the
  // viewer's active org.
  // ---------------------------------------------------------------------------

  admin: {
    /** Super-admin company list. */
    companies: () => ["companies"] as const,
    /** Super-admin view of one company (target id, not active org). */
    company: (companyId: string) => ["company", companyId] as const,
    /** Pending invites for a target company. */
    companyInvites: (companyId: string) => ["company-invites", companyId] as const,
    /** Per-company override settings page (target id). */
    companySettings: (companyId: string) => ["company-settings", companyId] as const,
    /** Per-company diagnostics: event stream. */
    companyDiagnosticsEvents: (
      companyId: string,
      meetingId: string | null,
      severity: string | null,
      source: string | null,
    ) => ["company-diagnostics", "events", companyId, meetingId, severity, source] as const,
    /** Per-company diagnostics: timeline. */
    companyDiagnosticsTimeline: (companyId: string, meetingId: string | null) =>
      ["company-diagnostics", "timeline", companyId, meetingId] as const,
    /** Platform provider-credits dashboard. */
    providerCredits: () => ["provider-credits"] as const,
    /** Cross-tenant activity overview (users, logins, recordings). */
    tenantUsage: () => ["admin", "tenant-usage"] as const,
    /** Platform billing / revenue overview (cost / charge / margin + credits) for a window. */
    platformBilling: (from: string, to: string) => ["admin", "platform-billing", from, to] as const,
    /** Pipeline graph for a template. */
    pipelineGraph: (template: string) => ["admin", "pipeline-graph", template] as const,
    /** Paginated recent pipeline runs (super-admin observability). */
    pipelineRuns: (limit: number, offset: number) => ["admin", "pipeline-runs", limit, offset] as const,
    /** One pipeline run detail (header + steps + graph). */
    pipelineRunDetail: (runId: string) => ["admin", "pipeline-run", runId] as const,
    /** System diagnostics: liveness/readiness health. */
    diagnosticsHealth: () => ["diagnostics", "health"] as const,
    /** System diagnostics: active streaming sessions. */
    diagnosticsSessions: () => ["diagnostics", "sessions"] as const,
    /** System diagnostics: orphaned resources. */
    diagnosticsOrphans: () => ["diagnostics", "orphans"] as const,
    /** System diagnostics: embedding service health. */
    diagnosticsEmbeddingHealth: () => ["diagnostics", "embedding-health"] as const,
    /** System diagnostics: recent logs. */
    diagnosticsLogs: () => ["diagnostics", "logs"] as const,
    /** System diagnostics: circuit-breaker state. */
    diagnosticsCircuitBreaker: () => ["diagnostics", "circuit-breaker"] as const,
    /** System diagnostics: filtered event stream. */
    diagnosticsEvents: (
      orgId: string | null,
      meetingId: string | null,
      severity: string | null,
      source: string | null,
    ) => ["diagnostics", "events", orgId, meetingId, severity, source] as const,
  },
} as const
